// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  IsoCurrencyCode,
  isoCurrencyCodeSchema,
} from '@/currency/constants/iso';
import {
  exceedsMaxCurrencyAmount,
  getAmountPrecisionMessage,
  parseCurrencyAmount,
} from '@/currency/utilities/validate';
import { MAX_DOLLAR_VALUE_LABEL } from '@/utilities/currency';
import { Decimal } from 'decimal.js';
import { z } from 'zod';

// The BE accepts and normalizes lowercase ISO codes; mirror that before the enum.
export function toUpperCaseIso(value: unknown): unknown {
  return typeof value === 'string' ? value.toUpperCase() : value;
}

// Wraps an ISO enum so a lower-case code off the wire normalizes before validation. ISO
// columns are CITEXT, so 'usd' is storable and would otherwise fail the whole payload.
// Takes the enum rather than exporting one constant per enum, so a new ISO enum needs no
// new helper.
export function normalizedIso<T extends z.ZodTypeAny>(schema: T) {
  return z.preprocess(toUpperCaseIso, schema);
}

function isFiniteDecimal(value: unknown): value is Decimal {
  return Decimal.isDecimal(value) && value.isFinite();
}

function addAmountPrecisionIssue(
  amount: Decimal,
  currencyCode: IsoCurrencyCode,
  ctx: z.RefinementCtx
) {
  const message = getAmountPrecisionMessage(amount, currencyCode);
  if (message !== null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['amount'], message });
  }
}

/**
 * Refines a form currency object whose amount stays a string: a blank amount
 * passes (it means "no value"), while an unparseable amount or one finer than
 * the currency's minor unit fails on the amount path.
 */
export function refineCurrencyAmountString(
  value: { amount: string; isoCode: IsoCurrencyCode },
  ctx: z.RefinementCtx
) {
  if (value.amount.trim() === '') {
    return;
  }

  const amount = parseCurrencyAmount(value.amount);
  if (amount === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['amount'],
      message: 'Amount must be a valid number',
    });
    return;
  }

  addAmountPrecisionIssue(amount, value.isoCode, ctx);
}

function addInvalidAmountIssue(ctx: z.RefinementCtx) {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: 'Amount must be a valid number',
  });

  return z.NEVER;
}

// Accepts an existing finite Decimal or a wire decimal string, following the
// dateSchema pattern (instance passthrough first, string transform second).
export const currencyAmountSchema = z.union([
  z.custom<Decimal>((value) => isFiniteDecimal(value), {
    message: 'Amount must be a valid number',
  }),
  z.string().transform((value, ctx) => {
    const amount = parseCurrencyAmount(value);
    return amount ?? addInvalidAmountIssue(ctx);
  }),
]);

/**
 * Parses the wire shape of the BE `currency.Currency` object — a banker's-
 * rounded 2-decimal amount string paired with its ISO 4217 code — into the
 * in-memory form holding a Decimal instance.
 */
export const currencySchema = z.object({
  amount: currencyAmountSchema,
  isoCode: normalizedIso(isoCurrencyCodeSchema),
});

/**
 * The core money value: a Decimal amount and its ISO currency code. Responses
 * parse into it, application logic works with it, and API functions take it.
 */
export type Currency = z.infer<typeof currencySchema>;

/** Schema for the value of a currency form field. */
export const serializableCurrencySchema = z.object({
  amount: z.string(),
  isoCode: isoCurrencyCodeSchema,
});

/**
 * App state for a money value whose amount must stay a string, such as the
 * value of an input control. `currencyInputSchema` turns it into a Currency on
 * submit. API functions take the Currency, never this type.
 */
export type SerializableCurrency = z.infer<typeof serializableCurrencySchema>;

const isoCurrencyCodeInputSchema = z.enum(isoCurrencyCodeSchema.options, {
  errorMap: (issue) => {
    if (issue.code === z.ZodIssueCode.invalid_type) {
      return { message: 'A currency is required' };
    }
    return { message: 'Select a valid currency' };
  },
});

/**
 * Outbound/form variant of the currency schema mirroring the BE
 * UnmarshalJSON rules: the amount is bounded, sub-cent
 * precision is rejected, and zero-decimal currencies must be whole units.
 * The amount stays a string base (form state) so the curated messages
 * surface, then parses to a Decimal in the output.
 */
export const currencyInputSchema = z
  .object({
    amount: z
      .string({
        required_error: 'This field is required',
        invalid_type_error: 'This field is required',
      })
      .min(1, 'This field is required')
      .transform((value, ctx) => {
        const amount = parseCurrencyAmount(value);
        return amount ?? addInvalidAmountIssue(ctx);
      }),
    isoCode: normalizedIso(isoCurrencyCodeInputSchema),
  })
  .superRefine((value, ctx) => {
    // A failed amount transform still reaches this refinement carrying a
    // non-Decimal value; the field already holds its own issue, so stop here.
    if (!isFiniteDecimal(value.amount)) {
      return;
    }
    if (exceedsMaxCurrencyAmount(value.amount)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['amount'],
        message: `Amount must not exceed ${MAX_DOLLAR_VALUE_LABEL}`,
      });
    }
    addAmountPrecisionIssue(value.amount, value.isoCode, ctx);
  });

/**
 * Clearable form variant of `currencyInputSchema`: an empty amount means
 * "clear the value" and parses to null; anything else must satisfy
 * `currencyInputSchema`.
 */
export const clearableCurrencyInputSchema = z
  .object({
    amount: z.string(),
    isoCode: normalizedIso(isoCurrencyCodeInputSchema),
  })
  .transform((value, ctx) => {
    if (value.amount.trim() === '') {
      return null;
    }
    const parsed = currencyInputSchema.safeParse(value);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        ctx.addIssue(issue);
      }
      return z.NEVER;
    }
    return parsed.data;
  });
